// Choosing which of a guide's places go into a trip (2026-10-17) — Build My
// Trip's "This guide's places" with "Let me review and choose the places".
// A plain list of names wasn't enough to choose from in an unfamiliar
// country, so each row shows the place's photo, category, visit length and
// the first sentence of its notes (all of it on tap), grouped by
// destination, with Select all / None per city and a running count. Uses
// only what the places already hold — nothing is looked up.
//
// Props: groups [{ title, places }], categories (the guide's), onCancel(),
// onConfirm(placeIds), user_info.

import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { notesText, firstSentence, visitLengthLabel, placeCategoryName, placePhotoURL } from "../../Common/placeRow";
import "./Modal.css";
import "./PlacesReviewModal.css";

const PlacesReviewModal = ({ groups, categories, onCancel, onConfirm, user_info }) => {
  const [selected, setSelected] = useState(() => new Set(groups.flatMap((g) => g.places.map((p) => String(p.ID)))));
  const [open, setOpen] = useState(() => new Set());
  const total = groups.reduce((n, g) => n + g.places.length, 0);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setGroup = (g, on) => setSelected((prev) => {
    const next = new Set(prev);
    g.places.forEach((p) => (on ? next.add(String(p.ID)) : next.delete(String(p.ID))));
    return next;
  });
  const toggleOpen = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const photoOf = (p) => placePhotoURL(p, user_info);
  const categoryOf = (p) => placeCategoryName(p, categories);

  return (
    <div className="modal">
      <Modal show={true} animation={false} centered dialogClassName="prw-dialog" onHide={onCancel} aria-labelledby="prw-title">
        <Modal.Body style={{ padding: 0 }}>
          <div className="prw-shell">
            <div className="prw-header">
              <h2 className="prw-title" id="prw-title">Places for this trip</h2>
              <span className="prw-count">{selected.size} of {total} selected</span>
              <button className="prw-close" onClick={onCancel} aria-label="Close">
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="prw-body">
              {groups.map((g) => {
                const inGroup = g.places.filter((p) => selected.has(String(p.ID))).length;
                return (
                  <section key={g.title} className="prw-group">
                    <div className="prw-group-head">
                      <span className="prw-group-title">{g.title} · {inGroup} of {g.places.length}</span>
                      <button className="prw-link" onClick={() => setGroup(g, true)}>Select all</button>
                      <button className="prw-link" onClick={() => setGroup(g, false)}>None</button>
                    </div>
                    {g.places.map((p) => {
                      const id = String(p.ID);
                      const text = notesText(p.Info);
                      const isOpen = open.has(id);
                      const meta = [categoryOf(p), visitLengthLabel(p.Duration)].filter(Boolean).join(" · ");
                      const photo = photoOf(p);
                      return (
                        <div key={id} className={`prw-row${selected.has(id) ? " on" : ""}`}>
                          <label className="prw-pick">
                            <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} aria-label={`Include ${p.Name}`} />
                          </label>
                          {photo ? <img className="prw-thumb" src={photo} alt="" loading="lazy" /> : <div className="prw-thumb prw-thumb-empty" aria-hidden="true" />}
                          <div className="prw-text" onClick={() => toggle(id)}>
                            <div className="prw-name">
                              {p.MustSee && <FontAwesomeIcon icon={solid("star")} className="prw-star" title="Must see" />}
                              {p.Name}
                            </div>
                            {meta && <div className="prw-meta">{meta}</div>}
                            {text && <div className={`prw-desc${isOpen ? " open" : ""}`}>{isOpen ? text : firstSentence(text)}</div>}
                          </div>
                          {text && text.length > firstSentence(text).length && (
                            <button className="prw-more" onClick={() => toggleOpen(id)} aria-label={isOpen ? "Show less" : "Show more"} aria-expanded={isOpen}>
                              <FontAwesomeIcon icon={isOpen ? solid("chevron-up") : solid("chevron-down")} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </section>
                );
              })}
            </div>

            <div className="prw-footer">
              <button className="tai-cancel-btn" onClick={onCancel}>Cancel</button>
              <button className="tai-primary-btn" disabled={selected.size === 0} onClick={() => onConfirm([...selected])}>
                Build with {selected.size} place{selected.size === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PlacesReviewModal;
